import { Table, message, Popover, Button, Select } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import { CheckCircleTwoTone } from "@ant-design/icons";
import EditButton from "../UI/EditButton/editbutton";
import { CountDetailsInterface } from "./Interfaces";
import { editCountItem } from "../../api/inventoryCount";
import { InputNumber } from "../UI/InputNumberComponent/inputnumber";
import { useLocation } from "react-router";

type prop = {
  tableKey: number;
  countsDetails: CountDetailsInterface[];
  countsDetailsSearch: CountDetailsInterface[];
  id: string | undefined;
  setCountsDetails: React.Dispatch<React.SetStateAction<CountDetailsInterface[]>>;
  setCountsDetailsSearch: React.Dispatch<React.SetStateAction<CountDetailsInterface[]>>;
  isLoading: boolean
}

const CountListDetails = ({ tableKey, countsDetails, id, setCountsDetails, setCountsDetailsSearch, isLoading, countsDetailsSearch }: prop) => {

  const { t } = useTranslation("common");

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const canEdit = searchParams.get('canEdit');

  const [selectedEditRow, setSelectedEditRow] = useState<number | null>(null);
  const [quantityOnHand, setQuantityOnHand] = useState<number | null>(null)
  const [justificationId, setJustification] = useState<number | null>(null)

  const { mutateAsync: editCountItemMutation, isLoading: editCountItemMutationLoading } = useMutation(
    "editCountItem",
    ({ id, countDetailId, quantityOnHand, justificationId }: { id: number, countDetailId: number, quantityOnHand: number | null, justificationId: number | null }) =>
      editCountItem({ id, countDetailId, quantityOnHand, justificationId }),
    {
      onSuccess: ({ data }) => {
        message.destroy()
        message.success(data.message)
        setCountsDetails(countsDetails.map((item: CountDetailsInterface) => {
          if (item.item.id === data.item.id) {
            const { message, ...updatedItem } = data;
            return updatedItem;
          }
          return item;
        }));
        setCountsDetailsSearch(countsDetailsSearch.map((item: CountDetailsInterface) => {
          if (item.item.id === data.item.id) {
            const { message, ...updatedItem } = data;
            return updatedItem;
          }
          return item;
        }));
        setSelectedEditRow(null)
      },
      onError: ({ response }) => {
        message.destroy()
        message.error(response.data.message)
        setSelectedEditRow(null)
      },
    }
  );

  const HandleEdit = (id: number, justification: { id: any; name: any }, quantityOnHand: any) => {
    setJustification(justification?.id ?? null)
    setSelectedEditRow(id)
    setQuantityOnHand(quantityOnHand)
  }

  const handleEditSave = (record: CountDetailsInterface, quantityOnHand: number | null) => {
    if (record.quantityOnHand == quantityOnHand &&
      ((record.justification !== null && record.justification.id === justificationId) ||
        (record.justification === null && record.justification === justificationId))
    ) {
      setSelectedEditRow(null)
      return
    }
    if (quantityOnHand != record.quantityInStock && !justificationId) {
      message.error(t("justification-is-required"))
      return
    }
    editCountItemMutation({ id: parseInt(id ?? '0'), countDetailId: record.item.id, quantityOnHand: quantityOnHand, justificationId: justificationId })
  }

  const columns: {}[] = [
    {
      title: `${t("category")}`,
      dataIndex: "category",
      key: "category",
      render: (category: string, record: CountDetailsInterface) => {
        return <div>{record.item.category.name}</div>
      }
    },
    {
      title: `${t("name")}`,
      dataIndex: "name",
      key: "name",
      className: "table-header",
      width: "8rem",
      render: (name: string, record: CountDetailsInterface) => {
        return <div>{record.item.name}</div>
      }
    },
    {
      title: `${t("barcode")}`,
      dataIndex: "barcode",
      key: "barcode",
      className: "table-header",
      width: "8rem",
      render: (barcode: string, record: CountDetailsInterface) => {
        return <div>{record.item.barcode}</div>
      }
    },
    {
      title: `${t("quantity-in-stock")}`,
      dataIndex: "quantityInStock",
      key: "quantityInStock",
      className: "table-header",
      width: "8rem",
      render: (quantityInStock: string, record: CountDetailsInterface) => {
        return <div>{quantityInStock}</div>
      }
    },
    {
      title: `${t("quantity-on-hand")}`,
      dataIndex: "quantityOnHand",
      key: "averageCost",
      width: "8rem",
      render: (qtyOnHand: number | null, record: CountDetailsInterface) => {
        return selectedEditRow === record.item.id ?
          <InputNumber
            maxLength={50}
            style={{ height: '30px' }}
            value={quantityOnHand?.toString()}
            onChange={(value: number | string | null | undefined) => setQuantityOnHand(value === '' ? null : value as number)}
            placeholder={qtyOnHand?.toFixed(2) ?? "null"}
          />
          : <div style={{ height: '30px', display: 'flex', alignItems: 'center' }}>{qtyOnHand}</div>
      }
    },
    {
      title: `${t("difference")}`,
      dataIndex: "difference",
      key: "difference",
      className: "table-header",
      width: "8rem",
      render: (difference: string, record: CountDetailsInterface) => {
        return selectedEditRow === record.item.id ?
          <div>{record.quantityInStock - (quantityOnHand ?? 0)}</div> :
          <div>{difference ?? '0'}</div>
      }
    },
    {
      title: `${t("justification")}`,
      dataIndex: "justification",
      key: "justification",
      className: "table-header",
      width: "8rem",
      render: (jstification: { id: number, name: string }, record: CountDetailsInterface) => {
        return selectedEditRow === record.item.id ?
          <Select
            onChange={(value: number) => setJustification(value ?? null)}
            style={{ width: '100%', height: '30px' }}
            defaultValue={jstification?.id}
            disabled={quantityOnHand == null || quantityOnHand == record.quantityOnHand || quantityOnHand == record.quantityInStock}
          >
            <Select.Option key={1} value={1}>
              Waste
            </Select.Option>
            <Select.Option key={2} value={2}>
              Stolen
            </Select.Option>
            <Select.Option key={3} value={3}>
              Gift
            </Select.Option>
            <Select.Option key={4} value={4}>
              Donation
            </Select.Option>
          </Select>
          :
          <div>{jstification?.name}</div>
      }
    },
    {
      title: `${t("action")}`,
      key: "action",
      width: "4rem",
      align: "center" as "center",
      render: (record: CountDetailsInterface) => (
        <>
          {selectedEditRow === record.item.id ? <Popover content={'Confirm changes'} mouseEnterDelay={0} mouseLeaveDelay={0}>
            <Button
              className="Button"
              type="text"
              icon={<CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: "14px" }} />}
              onClick={() => handleEditSave(record, quantityOnHand)}
              size={"small"}
            />
          </Popover> : <EditButton
            onClick={() => HandleEdit(record.item.id, record.justification, record.quantityOnHand)}
            disabled={canEdit === 'false'}
          />}

        </>
      ),
    },
  ];

  return (
    <>
      <Table
        scroll={{ x: 1000 }}
        key={tableKey}
        columns={columns}
        dataSource={countsDetails}
        rowKey={(record) => record.item.id}
        rowClassName={(record, index) =>
          index % 2 === 0
            ? "table-row-light table-row"
            : "table-row-dark table-row"
        }
        loading={isLoading || editCountItemMutationLoading}
      />
    </>
  );
};

export default CountListDetails;
